import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ResolvedWebsiteTheme } from './theme';
import { useWebsiteTheme } from './theme';
import { upsertMetaTag } from './head';

type WebsiteTitlebarColorScheme = 'light' | 'dark';
export type WebsiteTitlebarMode = 'match-theme' | WebsiteTitlebarColorScheme;

type WebsiteTitlebarEntry = {
  id: symbol;
  order: number;
  themeColor: string;
  colorScheme: WebsiteTitlebarColorScheme;
};

type WebsiteTitlebarRegistry = {
  register: (
    id: symbol,
    entry: Omit<WebsiteTitlebarEntry, 'id' | 'order'>
  ) => void;
  update: (
    id: symbol,
    entry: Omit<WebsiteTitlebarEntry, 'id' | 'order'>
  ) => void;
  unregister: (id: symbol) => void;
};

type UseWebsiteTitlebarOptions = {
  sourceRef?: React.RefObject<HTMLElement | null>;
  light?: string;
  dark?: string;
  colorScheme?: WebsiteTitlebarMode;
  observe?: boolean;
};

const DEFAULT_LIGHT_TITLEBAR_COLOR = '#ffffff';
const DEFAULT_DARK_TITLEBAR_COLOR = '#171717';
const TRANSPARENT_BACKGROUND_VALUES = new Set([
  '',
  'transparent',
  'rgba(0, 0, 0, 0)',
  'rgba(0,0,0,0)',
]);

const WebsiteTitlebarContext = createContext<WebsiteTitlebarRegistry | null>(
  null
);

function readOpaqueBackgroundColor(element: HTMLElement | null): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  let current: HTMLElement | null = element;
  while (current) {
    const value = window.getComputedStyle(current).backgroundColor.trim();
    if (!TRANSPARENT_BACKGROUND_VALUES.has(value)) {
      return value;
    }
    current = current.parentElement;
  }

  return null;
}

function fallbackThemeColor(resolvedTheme: ResolvedWebsiteTheme) {
  if (typeof document === 'undefined') {
    return resolvedTheme === 'dark'
      ? DEFAULT_DARK_TITLEBAR_COLOR
      : DEFAULT_LIGHT_TITLEBAR_COLOR;
  }

  return (
    readOpaqueBackgroundColor(document.body) ??
    readOpaqueBackgroundColor(document.documentElement) ??
    (resolvedTheme === 'dark'
      ? DEFAULT_DARK_TITLEBAR_COLOR
      : DEFAULT_LIGHT_TITLEBAR_COLOR)
  );
}

function applyTitlebarState(entry: {
  themeColor: string;
  colorScheme: WebsiteTitlebarColorScheme;
}) {
  upsertMetaTag('name', 'theme-color', entry.themeColor, {
    'data-cueboard-managed': 'website-titlebar',
  });
  upsertMetaTag('name', 'color-scheme', entry.colorScheme, {
    'data-cueboard-managed': 'website-titlebar',
  });

  document.documentElement.style.colorScheme = entry.colorScheme;
  document.documentElement.style.setProperty(
    '--website-titlebar-color',
    entry.themeColor
  );
  document.documentElement.style.setProperty(
    '--website-page-chrome-color',
    entry.themeColor
  );
  document.documentElement.style.backgroundColor = entry.themeColor;
  document.documentElement.dataset.websiteColorScheme = entry.colorScheme;

  if (document.body) {
    document.body.style.backgroundColor = entry.themeColor;
    document.body.style.setProperty(
      '--website-page-chrome-color',
      entry.themeColor
    );
  }
}

function resolveActiveEntry(
  entries: Map<symbol, WebsiteTitlebarEntry>
): WebsiteTitlebarEntry | null {
  let activeEntry: WebsiteTitlebarEntry | null = null;

  for (const entry of entries.values()) {
    if (!activeEntry || entry.order > activeEntry.order) {
      activeEntry = entry;
    }
  }

  return activeEntry;
}

export function WebsiteTitlebarProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { resolvedTheme } = useWebsiteTheme();
  const entriesRef = useRef(new Map<symbol, WebsiteTitlebarEntry>());
  const nextOrderRef = useRef(0);
  const [revision, setRevision] = useState(0);

  const bumpRevision = useCallback(() => {
    setRevision(current => current + 1);
  }, []);

  const register = useCallback<WebsiteTitlebarRegistry['register']>(
    (id, entry) => {
      const existing = entriesRef.current.get(id);

      entriesRef.current.set(id, {
        id,
        order: existing?.order ?? ++nextOrderRef.current,
        ...entry,
      });

      bumpRevision();
    },
    [bumpRevision]
  );

  const update = useCallback<WebsiteTitlebarRegistry['update']>(
    (id, entry) => {
      const existing = entriesRef.current.get(id);
      if (!existing) {
        register(id, entry);
        return;
      }

      if (
        existing.themeColor === entry.themeColor &&
        existing.colorScheme === entry.colorScheme
      ) {
        return;
      }

      entriesRef.current.set(id, {
        ...existing,
        ...entry,
      });

      bumpRevision();
    },
    [bumpRevision, register]
  );

  const unregister = useCallback<WebsiteTitlebarRegistry['unregister']>(
    id => {
      if (!entriesRef.current.delete(id)) {
        return;
      }

      bumpRevision();
    },
    [bumpRevision]
  );

  const registry = useMemo<WebsiteTitlebarRegistry>(
    () => ({
      register,
      update,
      unregister,
    }),
    [register, unregister, update]
  );

  useEffect(() => {
    const activeEntry = resolveActiveEntry(entriesRef.current);

    applyTitlebarState({
      themeColor: activeEntry?.themeColor ?? fallbackThemeColor(resolvedTheme),
      colorScheme: activeEntry?.colorScheme ?? resolvedTheme,
    });
  }, [resolvedTheme, revision]);

  return (
    <WebsiteTitlebarContext.Provider value={registry}>
      {children}
    </WebsiteTitlebarContext.Provider>
  );
}

function resolveRequestedColorScheme(
  mode: WebsiteTitlebarMode,
  resolvedTheme: ResolvedWebsiteTheme
): WebsiteTitlebarColorScheme {
  return mode === 'match-theme' ? resolvedTheme : mode;
}

function resolveRequestedThemeColor(params: {
  resolvedTheme: ResolvedWebsiteTheme;
  light?: string;
  dark?: string;
  sourceRef?: React.RefObject<HTMLElement | null>;
}) {
  const preferredColor =
    params.resolvedTheme === 'dark'
      ? params.dark?.trim()
      : params.light?.trim();
  if (preferredColor) {
    return preferredColor;
  }

  const sharedColor = params.light?.trim() || params.dark?.trim();
  if (sharedColor) {
    return sharedColor;
  }

  return (
    readOpaqueBackgroundColor(params.sourceRef?.current ?? null) ??
    fallbackThemeColor(params.resolvedTheme)
  );
}

export function useWebsiteTitlebar(options: UseWebsiteTitlebarOptions = {}) {
  const registry = useContext(WebsiteTitlebarContext);
  const { resolvedTheme } = useWebsiteTheme();
  const titlebarIdRef = useRef(Symbol('website-titlebar'));
  const computeEntryRef = useRef<
    () => Omit<WebsiteTitlebarEntry, 'id' | 'order'>
  >(() => ({
    themeColor: fallbackThemeColor(resolvedTheme),
    colorScheme: resolvedTheme,
  }));

  const {
    sourceRef,
    light,
    dark,
    colorScheme = 'match-theme',
    observe = true,
  } = options;

  const computeEntry = useCallback(
    (): Omit<WebsiteTitlebarEntry, 'id' | 'order'> => ({
      themeColor: resolveRequestedThemeColor({
        resolvedTheme,
        light,
        dark,
        sourceRef,
      }),
      colorScheme: resolveRequestedColorScheme(colorScheme, resolvedTheme),
    }),
    [colorScheme, dark, light, resolvedTheme, sourceRef]
  );

  if (!registry) {
    throw new Error(
      'useWebsiteTitlebar must be used within WebsiteTitlebarProvider'
    );
  }

  useEffect(() => {
    computeEntryRef.current = computeEntry;
  }, [computeEntry]);

  useEffect(() => {
    const titlebarId = titlebarIdRef.current;
    registry.register(titlebarId, computeEntryRef.current());

    return () => {
      registry.unregister(titlebarId);
    };
  }, [registry]);

  useEffect(() => {
    registry.update(titlebarIdRef.current, computeEntry());
  }, [computeEntry, registry]);

  useEffect(() => {
    if (
      !observe ||
      !sourceRef?.current ||
      typeof MutationObserver === 'undefined'
    ) {
      return;
    }

    const sync = () => {
      registry.update(titlebarIdRef.current, computeEntry());
    };

    const elementObserver = new MutationObserver(sync);
    elementObserver.observe(sourceRef.current, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    const rootObserver = new MutationObserver(sync);
    rootObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      elementObserver.disconnect();
      rootObserver.disconnect();
    };
  }, [computeEntry, observe, registry, sourceRef]);
}
